import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Search,
  Power,
  PowerOff,
  ShoppingBag,
  BookOpen,
  Radio,
  ShieldCheck,
  UserCheck,
  Sparkles,
  Zap,
  MessageSquare,
  Heart,
  Calendar,
  XCircle,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import api, { formatApiError } from "@/lib/api";

const Metric = ({ label, value }) => (
  <div className="kd9-card p-4">
    <div
      className="font-mont text-[9px] uppercase tracking-widest"
      style={{ color: "var(--kd9-text-soft)" }}
    >
      {label}
    </div>
    <div className="font-cinzel text-2xl mt-1 kd9-gold-text">{value ?? 0}</div>
  </div>
);

const CAPABILITY_ICONS = {
  diagnostics: Sparkles,
  quick_consultation: Zap,
  talk_with_consultant: MessageSquare,
  human_accompaniment: Heart,
  video_calls: Calendar,
  oracle_readings: ShieldCheck,
  temple_live: Radio,
};

const capabilityCount = (row) =>
  Object.values(row?.capabilities?.granted || {}).filter(Boolean).length;

const DIRECT_SERVICE_LABELS = {
  quick_consultation: "Consulta Rápida",
  talk_with_consultant: "Hablar con un Consultor",
};

const CapabilityControl = ({
  item,
  row,
  busy,
  onToggle,
}) => {
  const granted = Boolean(row?.capabilities?.granted?.[item.key]);
  const effective = Boolean(row?.capabilities?.effective?.[item.key]);
  const readiness = row?.service_readiness?.[item.key] || null;
  const profileRequired = Boolean(readiness?.profile_required);
  const profileBlocked = Boolean(
    granted && effective && readiness?.status === "profile_incomplete"
  );
  const available = readiness ? Boolean(readiness.available) : effective;
  const Icon = CAPABILITY_ICONS[item.key] || ShieldCheck;
  const changing = busy === `capability:${row.id}:${item.key}`;
  const locked = !row.consultant_approved;

  return (
    <div
      className="border rounded-xl p-4 flex flex-col gap-3"
      style={{
        borderColor: granted ? "rgba(212,175,55,.45)" : "var(--kd9-border)",
        background: granted ? "rgba(212,175,55,.04)" : "transparent",
      }}
      data-testid={`consultant-capability-${row.id}-${item.key}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            border: "1px solid var(--kd9-border)",
            color: granted ? "var(--kd9-gold)" : "var(--kd9-text-soft)",
          }}
        >
          <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-cinzel text-sm">{item.label}</div>
          <div
            className="font-cormorant text-sm leading-snug mt-1"
            style={{ color: "var(--kd9-text-soft)" }}
          >
            {item.description}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-[9px] font-mont uppercase tracking-wider">
          {!granted && (
            <span style={{ color: "var(--kd9-text-soft)" }}>DESACTIVADA</span>
          )}
          {granted && effective && profileBlocked && (
            <span style={{ color: "#FCD34D" }}>ACTIVA · BLOQUEADA POR PERFIL</span>
          )}
          {granted && effective && !profileBlocked && profileRequired && available && (
            <span style={{ color: "#86EFAC" }}>ACTIVA Y DISPONIBLE</span>
          )}
          {granted && effective && !profileRequired && (
            <span style={{ color: "#86EFAC" }}>ACTIVA Y OPERATIVA</span>
          )}
          {granted && !effective && (
            <span style={{ color: "#FCD34D" }}>CONCEDIDA · NO OPERATIVA</span>
          )}
        </div>

        <button
          type="button"
          aria-pressed={granted}
          disabled={changing || locked}
          onClick={() => onToggle(row, item, !granted)}
          className={granted ? "kd9-btn-primary px-3 py-2 rounded-lg text-[10px]" : "kd9-btn-outline px-3 py-2 rounded-lg text-[10px]"}
          data-testid={`consultant-capability-toggle-${row.id}-${item.key}`}
        >
          {changing ? "Guardando..." : granted ? "Desactivar" : "Activar"}
        </button>
      </div>

      {profileBlocked && (
        <div
          className="rounded-lg border px-3 py-2 text-[10px] font-mont"
          style={{ borderColor: "rgba(245,158,11,.30)", color: "#FCD34D" }}
          data-testid={`consultant-capability-profile-block-${row.id}-${item.key}`}
        >
          Completar perfil para publicarlo: {(readiness?.profile_missing || []).join(", ")}
        </div>
      )}
    </div>
  );
};

const TeamManagement = ({ role = "consultant", title = "CONSULTORES" }) => {
  const creatorMode = role === "creator";
  const consultantMode = role === "consultant";

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      if (consultantMode) {
        const [governanceResponse, catalogResponse] = await Promise.all([
          api.get("/api/director/consultants"),
          api.get("/api/director/consultant-capabilities/catalog"),
        ]);
        setRows(governanceResponse.data?.items || []);
        setSummary(governanceResponse.data?.summary || {});
        setCatalog(catalogResponse.data?.items || []);
        return;
      }

      if (creatorMode) {
        const { data } = await api.get("/api/director/creators");
        setRows(data.items || []);
        setSummary(data.summary || {});
        setCatalog([]);
        return;
      }

      const { data } = await api.get("/api/admin/users", {
        params: { role, limit: 500 },
      });
      setRows(data || []);
      setSummary(null);
      setCatalog([]);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [consultantMode, creatorMode, role]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      `${row.kd9_id || ""} ${row.name || ""} ${row.surname || ""} ${row.email || ""}`
        .toLowerCase()
        .includes(needle)
    );
  }, [rows, search]);

  const setCreatorState = async (row, enabled) => {
    setBusy(row.id);
    setError("");
    setOk("");
    try {
      await api.post(
        `/api/director/creators/${row.id}/${enabled ? "reactivate" : "suspend"}`
      );
      setOk(`${row.email}: Creador ${enabled ? "reactivado" : "suspendido"}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const setConsultantAccount = async (row, enabled) => {
    setBusy(`account:${row.id}`);
    setError("");
    setOk("");
    try {
      await api.post(
        `/api/director/users/${row.id}/${enabled ? "activate" : "suspend"}`
      );
      setOk(`${row.email}: cuenta ${enabled ? "reactivada" : "suspendida"}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const toggleCapability = async (row, item, enabled) => {
    if (!row.consultant_approved) {
      setError("El Consultor debe estar aprobado antes de recibir capacidades.");
      return;
    }

    const reason = window.prompt(
      `Motivo para ${enabled ? "activar" : "desactivar"} “${item.label}”:`,
      enabled
        ? "Habilitación individual autorizada por Dirección KD9"
        : "Desactivación individual autorizada por Dirección KD9"
    );

    if (reason === null) return;
    if (reason.trim().length < 5) {
      setError("El motivo debe tener al menos 5 caracteres.");
      return;
    }

    setBusy(`capability:${row.id}:${item.key}`);
    setError("");
    setOk("");

    try {
      await api.put(
        `/api/director/consultants/${row.id}/capabilities/${item.key}`,
        {
          enabled,
          reason: reason.trim(),
        }
      );
      setOk(
        enabled && DIRECT_SERVICE_LABELS[item.key] && row.profile_complete === false
          ? `${row.email}: ${item.label} activada. KD9 notificó al Consultor para completar sus datos antes de publicarlo a los usuarios.`
          : `${row.email}: ${item.label} ${enabled ? "activada" : "desactivada"}.`
      );
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const toggleOracleGuide = async (row) => {
    const enabled = !Boolean(row.oracle_guide_enabled);
    setBusy(`oracle-guide:${row.id}`);
    setError("");
    setOk("");
    try {
      await api.put(`/api/director/consultants/${row.id}/oracle-guide`, { enabled });
      setOk(`${row.email}: guía de significados ${enabled ? "habilitada" : "deshabilitada"}.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  const disableAllCapabilities = async (row) => {
    if (!catalog.length || capabilityCount(row) === 0) return;

    const reason = window.prompt(
      "Motivo para desactivar todas las capacidades de este Consultor:",
      "Desactivación total autorizada por Dirección KD9"
    );

    if (reason === null) return;
    if (reason.trim().length < 5) {
      setError("El motivo debe tener al menos 5 caracteres.");
      return;
    }

    const capabilities = Object.fromEntries(
      catalog.map((item) => [item.key, false])
    );

    setBusy(`capabilities-all:${row.id}`);
    setError("");
    setOk("");

    try {
      await api.put(
        `/api/director/consultants/${row.id}/capabilities`,
        {
          capabilities,
          reason: reason.trim(),
        }
      );
      setOk(`${row.email}: todas las capacidades fueron desactivadas.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy("");
    }
  };

  if (consultantMode) {
    return (
      <PageShell
        kicker="Director KD9 · Gobierno individual"
        title="GOBIERNO DE CONSULTORES"
        desc="KD9 mantiene un único rol Consultor. Dirección decide, persona por persona, exactamente qué servicios puede prestar cada Consultor."
        badge={
          <div className="flex gap-2">
            <Link
              to="/app/solicitudes-roles"
              className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]"
            >
              Solicitudes
            </Link>
            <button
              onClick={load}
              className="kd9-btn-outline p-2 rounded-lg"
              aria-label="Actualizar Gobierno de Consultores"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        }
        testId="consultant-governance-page"
      >
        {error && (
          <div className="p-3 mb-4 border border-red-800 text-red-300 rounded-lg">
            {error}
          </div>
        )}
        {ok && (
          <div
            className="p-3 mb-4 border rounded-lg"
            style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,.35)" }}
          >
            {ok}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-3 mb-5">
          <Metric label="Consultores" value={summary?.total} />
          <Metric label="Cuentas activas" value={summary?.active} />
          <Metric label="Perfiles listos" value={summary?.profiles_ready} />
          <Metric label="Con capacidades" value={summary?.consultants_with_capabilities} />
          <Metric label="Capacidades concedidas" value={summary?.capabilities_enabled_total} />
          <Metric label="Capacidades disponibles" value={summary?.available_services_total} />
          <Metric label="Solicitudes pendientes" value={summary?.pending_requests} />
        </div>

        <div
          className="kd9-card p-4 mb-5 flex items-center gap-3"
          data-testid="consultant-governance-search"
        >
          <Search size={15} />
          <input
            className="bg-transparent outline-none flex-1"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar ID KD9, nombre o correo"
          />
        </div>

        <div className="space-y-4">
          {filtered.length === 0 ? (
            <div className="kd9-card p-8 text-center">
              No existen Consultores para mostrar.
            </div>
          ) : (
            filtered.map((row) => {
              const grantedCount = capabilityCount(row);
              const blockedDirectServices = row.blocked_direct_services || [];
              const publicationBlocked = blockedDirectServices.length > 0;
              const missingPhoto = (row.profile_missing || []).some((item) =>
                String(item).toLowerCase().includes("fotografía")
              );
              return (
                <section
                  key={row.id}
                  className="kd9-card p-5"
                  data-testid={`consultant-governance-row-${row.id}`}
                >
                  <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-cinzel kd9-gold-text text-lg">
                        {row.name} {row.surname || ""}
                      </div>
                      <div
                        className="text-xs mt-1"
                        style={{ color: "var(--kd9-text-soft)" }}
                      >
                        {row.email} · {row.kd9_id || "Sin ID KD9"}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-3 text-[9px] font-mont uppercase tracking-wider">
                        {row.is_ai_consultant && (
                          <span
                            className="border rounded-full px-2 py-1"
                            style={{
                              borderColor: "rgba(34,211,238,.45)",
                              color: "#67E8F9",
                              background: "rgba(34,211,238,.08)",
                            }}
                            data-testid={`consultant-ai-badge-${row.id}`}
                          >
                            🤖 {row.ai_identity?.disclosure_label || "CONSULTOR IA KD9"}
                          </span>
                        )}
                        <span
                          className="border rounded-full px-2 py-1"
                          style={{
                            borderColor: "var(--kd9-border)",
                            color: row.status === "active" ? "#86EFAC" : "#FCA5A5",
                          }}
                        >
                          {row.status === "active"
                            ? "CUENTA ACTIVA"
                            : String(row.status || "inactive").toUpperCase()}
                        </span>

                        <span
                          className="border rounded-full px-2 py-1"
                          style={{
                            borderColor: "var(--kd9-border)",
                            color: row.consultant_approved ? "#86EFAC" : "#FCD34D",
                          }}
                        >
                          {row.consultant_approved
                            ? "CONSULTOR APROBADO"
                            : "PENDIENTE DE APROBACIÓN"}
                        </span>

                        <span
                          className="border rounded-full px-2 py-1"
                          style={{
                            borderColor: "var(--kd9-border)",
                            color: row.profile_complete ? "#86EFAC" : "#FCA5A5",
                          }}
                        >
                          {row.profile_complete ? "PERFIL LISTO" : "PERFIL INCOMPLETO"}
                        </span>

                        <span
                          className="border rounded-full px-2 py-1"
                          style={{ borderColor: "var(--kd9-border)" }}
                        >
                          {grantedCount}/{catalog.length || 7} CAPACIDADES
                        </span>
                      </div>

                      {row.is_ai_consultant && (
                        <div className="text-[10px] mt-2 space-y-1" style={{ color: "#67E8F9" }}>
                          <div>
                            Identidad: {row.ai_identity?.identity_version || "IA KD9"} · Alcance actual: {(row.ai_identity?.operational_scope || []).join(", ") || "sin capacidades"}
                          </div>
                          <div data-testid={`consultant-ai-knowledge-${row.id}`}>
                            Conocimiento: {row.ai_knowledge_version || "pendiente"} · {String(row.ai_knowledge_status || "sin estado").toUpperCase()}
                          </div>
                          <div data-testid={`consultant-ai-diagnostics-${row.id}`}>
                            Motor diagnósticos: {row.ai_diagnostic_engine_version || "pendiente"} · {String(row.ai_diagnostic_engine_status || "sin estado").toUpperCase()}
                          </div>
                          <div data-testid={`consultant-ai-training-${row.id}`}>
                            Entrenamiento: {row.ai_training_version || "pendiente"} · Revisión humana {row.ai_diagnostic_review_required ? "OBLIGATORIA" : "NO REQUERIDA"}
                          </div>
                          <div data-testid={`consultant-ai-fallback-${row.id}`}>
                            Fallback: {row.ai_diagnostic_fallback_version || "pendiente"} · {String(row.ai_diagnostic_fallback_provider || "sin proveedor").toUpperCase()} · {row.ai_diagnostic_fallback_model || "sin modelo"} · Humanos {row.ai_diagnostic_fallback_minutes ?? "—"} min · {row.ai_diagnostic_fallback_operational ? "OPERATIVO" : row.ai_diagnostic_fallback_provider_configured ? "EN ESPERA" : "PENDIENTE DE CLAVE"}
                          </div>
                          <div data-testid={`consultant-ai-settlement-${row.id}`}>
                            Liquidación IA: {String(row.ai_diagnostic_settlement_mode || "pendiente").toUpperCase()} · Ganancia personal IA: NO
                          </div>
                        </div>
                      )}

                      {!row.profile_complete && (
                        <div className="text-[10px] mt-2" style={{ color: "#FCA5A5" }}>
                          Falta: {(row.profile_missing || []).join(", ") || "completar perfil profesional"}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        disabled={busy === `account:${row.id}`}
                        onClick={() =>
                          setConsultantAccount(row, row.status !== "active")
                        }
                        className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]"
                        data-testid={`consultant-account-${row.id}`}
                      >
                        {row.status === "active" ? (
                          <>
                            <PowerOff size={12} className="inline mr-1" />
                            Suspender cuenta
                          </>
                        ) : (
                          <>
                            <Power size={12} className="inline mr-1" />
                            Reactivar cuenta
                          </>
                        )}
                      </button>

                      {grantedCount > 0 && (
                        <button
                          disabled={busy === `capabilities-all:${row.id}`}
                          onClick={() => disableAllCapabilities(row)}
                          className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]"
                          data-testid={`consultant-disable-all-${row.id}`}
                        >
                          <XCircle size={12} className="inline mr-1" />
                          Desactivar todas
                        </button>
                      )}
                    </div>
                  </div>

                  {row.consultant_approved && (
                    <div
                      className="mt-4 border rounded-xl p-4"
                      style={{
                        borderColor: publicationBlocked
                          ? "rgba(245,158,11,.45)"
                          : row.profile_complete
                            ? "rgba(34,197,94,.35)"
                            : "var(--kd9-border)",
                        background: publicationBlocked
                          ? "rgba(245,158,11,.05)"
                          : "rgba(212,175,55,.025)",
                      }}
                      data-testid={`consultant-publication-state-${row.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 font-cinzel kd9-gold-text text-sm">
                            {publicationBlocked ? (
                              <AlertTriangle size={15} />
                            ) : row.profile_complete ? (
                              <CheckCircle2 size={15} />
                            ) : (
                              <UserCheck size={15} />
                            )}
                            PUBLICACIÓN PARA USUARIOS
                          </div>

                          {publicationBlocked ? (
                            <>
                              <div className="font-mont text-[10px] uppercase tracking-widest mt-2" style={{ color: "#FCD34D" }}>
                                CAPACIDAD ACTIVADA · NO VISIBLE AL USUARIO
                              </div>
                              <p className="font-cormorant text-base mt-2" style={{ color: "var(--kd9-text-soft)" }}>
                                Servicios bloqueados: {blockedDirectServices.map((key) => DIRECT_SERVICE_LABELS[key] || key).join(", ")}.
                                Al completar los requisitos, KD9 los publica automáticamente sin volver a activar la capacidad.
                              </p>
                            </>
                          ) : row.profile_complete ? (
                            <div className="font-mont text-[10px] uppercase tracking-widest mt-2" style={{ color: "#86EFAC" }}>
                              PERFIL LISTO · LOS SERVICIOS AUTORIZADOS PUEDEN PUBLICARSE
                            </div>
                          ) : (
                            <div className="font-mont text-[10px] uppercase tracking-widest mt-2" style={{ color: "#FCD34D" }}>
                              PERFIL PENDIENTE · COMPLETAR ANTES DE PUBLICAR SERVICIOS DIRECTOS
                            </div>
                          )}

                          {!row.profile_complete && (
                            <div className="mt-3 text-xs">
                              <span className="font-mont uppercase tracking-wider">Falta:</span>{" "}
                              {(row.profile_missing || []).join(", ") || "completar perfil profesional"}.
                            </div>
                          )}
                        </div>

                        <div className="shrink-0 max-w-sm">
                          {!row.profile_complete && (
                            <div
                              className="rounded-lg border px-3 py-2 text-[10px] font-mont"
                              style={{ borderColor: "rgba(245,158,11,.30)", color: "#FCD34D" }}
                              data-testid={`consultant-profile-handoff-${row.id}`}
                            >
                              EL CONSULTOR DEBE COMPLETAR SU INFORMACIÓN
                              <div className="mt-1 normal-case tracking-normal" style={{ color: "var(--kd9-text-soft)" }}>
                                Al activar un servicio que requiere perfil público, KD9 envía un aviso al Consultor y mantiene visible la pestaña “Activar mis servicios” hasta completar los requisitos.
                              </div>
                            </div>
                          )}
                          {missingPhoto && (
                            <div className="mt-2 text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
                              La fotografía la envía el Consultor. Dirección únicamente revisa y aprueba la solicitud recibida.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {!row.consultant_approved && (
                    <div
                      className="mt-4 border rounded-lg p-3 text-sm"
                      style={{
                        borderColor: "rgba(245,158,11,.35)",
                        color: "#FCD34D",
                      }}
                    >
                      Dirección debe aprobar primero el rol Consultor. Hasta entonces
                      las capacidades permanecen bloqueadas.
                    </div>
                  )}

                  <div className="mt-5 rounded-xl border p-4" style={{borderColor:"var(--kd9-border)"}} data-testid={`consultant-oracle-guide-${row.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <h3 className="font-cinzel kd9-gold-text text-sm">Ayuda de significados del Oráculo</h3>
                        <p className="text-xs mt-2" style={{color:"var(--kd9-text-soft)"}}>
                          Permiso individual de Dirección. Si lo desactivas, el consultor puede leer las cartas y responder, pero no recibirá la biblioteca privada, significados ni sugerencias de interpretación.
                        </p>
                      </div>
                      <button type="button" disabled={Boolean(busy)} onClick={() => toggleOracleGuide(row)}
                        className={`${row.oracle_guide_enabled ? "kd9-btn-primary" : "kd9-btn-outline"} min-h-11 px-4 py-2 rounded-lg text-xs shrink-0 disabled:opacity-50`}
                        data-testid={`consultant-oracle-guide-toggle-${row.id}`}>
                        {row.oracle_guide_enabled ? "AYUDA ACTIVADA" : "ACTIVAR AYUDA"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <UserCheck size={15} style={{ color: "var(--kd9-gold)" }} />
                      <h3 className="font-cinzel kd9-gold-text">
                        Capacidades autorizadas por Dirección
                      </h3>
                    </div>

                    <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-3">
                      {catalog.map((item) => (
                        <CapabilityControl
                          key={item.key}
                          item={item}
                          row={row}
                          busy={busy}
                          onToggle={toggleCapability}
                        />
                      ))}
                    </div>
                  </div>
                </section>
              );
            })
          )}
        </div>
      </PageShell>
    );
  }

  if (creatorMode) {
    return (
      <PageShell
        kicker="Director KD9 · Gobierno exclusivo"
        title="GOBIERNO DE CREADORES"
        desc="Dirección controla aprobación, estado operativo, Marketplace y contenidos de Academia. Creador es una identidad independiente: no hereda Usuario ni Consultor."
        badge={
          <div className="flex gap-2">
            <Link
              to="/app/solicitudes-roles"
              className="kd9-btn-outline px-3 py-2 rounded-lg text-[10px]"
            >
              Solicitudes
            </Link>
            <button
              onClick={load}
              className="kd9-btn-outline p-2 rounded-lg"
              aria-label="Actualizar Gobierno de Creadores"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        }
        testId="creator-governance-page"
      >
        {error && (
          <div className="p-3 mb-4 border border-red-800 text-red-300 rounded-lg">
            {error}
          </div>
        )}
        {ok && (
          <div
            className="p-3 mb-4 border rounded-lg"
            style={{ color: "#86EFAC", borderColor: "rgba(34,197,94,.35)" }}
          >
            {ok}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <Metric label="Creadores" value={summary?.total} />
          <Metric label="Activos" value={summary?.active} />
          <Metric label="Suspendidos" value={summary?.suspended} />
          <Metric label="Productos pendientes" value={summary?.pending_products} />
          <Metric label="Cursos pendientes" value={summary?.pending_courses} />
        </div>

        <div className="kd9-card p-4 mb-5 flex items-center gap-3">
          <Search size={15} />
          <input
            className="bg-transparent outline-none flex-1"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar ID KD9, nombre o correo"
          />
        </div>

        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="kd9-card p-8 text-center">
              No existen Creadores aprobados.
            </div>
          ) : (
            filtered.map((row) => (
              <div key={row.id} className="kd9-card p-5">
                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-cinzel kd9-gold-text">
                      {row.name} {row.surname || ""}
                    </div>
                    <div
                      className="text-xs mt-1"
                      style={{ color: "var(--kd9-text-soft)" }}
                    >
                      {row.email} · {row.kd9_id || "Sin ID KD9"}
                    </div>
                    <div
                      className="font-mont text-[9px] uppercase tracking-widest mt-2"
                      style={{ color: row.creator_active ? "#86EFAC" : "#FCA5A5" }}
                    >
                      {row.creator_active ? "CREADOR ACTIVO" : "CREADOR SUSPENDIDO"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] min-w-[340px]">
                    <div
                      className="border rounded-lg p-2"
                      style={{ borderColor: "var(--kd9-border)" }}
                    >
                      <ShoppingBag size={12} className="inline mr-1" />{" "}
                      {row.products_live}/{row.products_total}
                      <div style={{ color: "var(--kd9-text-soft)" }}>
                        productos · {row.products_pending} pendientes
                      </div>
                    </div>
                    <div
                      className="border rounded-lg p-2"
                      style={{ borderColor: "var(--kd9-border)" }}
                    >
                      <BookOpen size={12} className="inline mr-1" />{" "}
                      {row.courses_live}/{row.courses_total}
                      <div style={{ color: "var(--kd9-text-soft)" }}>
                        cursos · {row.courses_pending} pendientes
                      </div>
                    </div>
                    <Link
                      to="/app/super-marketplace"
                      className="kd9-btn-outline rounded-lg px-3 py-2 flex items-center justify-center"
                    >
                      Marketplace
                    </Link>
                    <Link
                      to="/app/revision-contenidos-creador"
                      className="kd9-btn-outline rounded-lg px-3 py-2 flex items-center justify-center"
                    >
                      Academia
                    </Link>
                  </div>

                  <button
                    disabled={busy === row.id}
                    onClick={() => setCreatorState(row, !row.creator_active)}
                    className={
                      row.creator_active
                        ? "kd9-btn-outline px-4 py-3 rounded-xl text-[10px]"
                        : "kd9-btn-primary px-4 py-3 rounded-xl text-[10px]"
                    }
                    data-testid={`creator-state-${row.id}`}
                  >
                    {row.creator_active ? (
                      <>
                        <PowerOff size={13} className="inline mr-1" /> Suspender Creador
                      </>
                    ) : (
                      <>
                        <Power size={13} className="inline mr-1" /> Reactivar Creador
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      kicker="Director KD9 · Equipo real"
      title={title}
      desc="Cuentas existentes y estados calculados desde usuarios. La gestión estratégica continúa protegida por permisos del Director."
      badge={
        <button
          onClick={load}
          className="kd9-btn-outline p-2 rounded-lg"
          aria-label="Actualizar"
        >
          <RefreshCw size={14} />
        </button>
      }
    >
      {error && (
        <div className="p-3 mb-4 border border-red-800 text-red-300 rounded-lg">
          {error}
        </div>
      )}
      <div className="kd9-card p-4 mb-5 flex items-center gap-3">
        <Search size={15} />
        <input
          className="bg-transparent outline-none flex-1"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar nombre o correo"
        />
      </div>
      <div className="kd9-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center">No existen cuentas con este rol.</div>
        ) : (
          filtered.map((row) => (
            <div
              key={row.id}
              className="p-4 border-b last:border-0 grid md:grid-cols-[1fr_1fr_auto] gap-3"
              style={{ borderColor: "var(--kd9-border)" }}
            >
              <div>
                <strong>
                  {row.name} {row.surname || ""}
                </strong>
                <div className="text-xs">{row.email}</div>
              </div>
              <div className="text-xs">
                {(row.roles || []).join(", ")}
                {Object.keys(row.flags || {}).length > 0 &&
                  ` · ${Object.keys(row.flags).join(", ")}`}
              </div>
              <span className="text-[10px] uppercase tracking-widest">
                {row.status}
              </span>
            </div>
          ))
        )}
      </div>
    </PageShell>
  );
};

export default TeamManagement;
